import React, {
  HTMLAttributes,
  useContext,
  useEffect,
  useState,
  ReactElement,
} from "react";
import styled, { css } from "styled-components";
import { Field, FieldProps } from "formik";
import {
  Box,
  Link,
  Heading,
  ThemeType,
  TextField,
  Collapse,
  Text,
} from "@cruk/cruk-react-components";
// CONTEXT
import FormNameContext from "../../contexts/FormNameContext";
import DonationContext from "../../contexts/DonationContext";
import ThemeNameContext from "../../contexts/ThemeNameContext";
import VersionsContext from "../../contexts/VersionsContext";
// CONFIG
import { getFormAdmin, getFormComponents } from "../../configs/single-donation";
// HELPERS
import { FormPropsType } from "../../helpers/types/shared";
import useDataLayer from "../../helpers/useDataLayer";
import { getValidFormVersions } from "../../helpers/functions/single-donation";
// COMPONENTS
import FormGroup from "../FormGroup";
import FormFields from "../FormFields";
import FieldErrorMessage from "../FieldErrorMessage";
// DATA
import donationFrequencies from "../../data/donationFrequencies";
// STYLES
import { RequiredParagraph, StyledCollapse } from "../styles";

type AmountLabelProps = HTMLAttributes<HTMLDivElement> & {
  formTheme: string;
  theme?: ThemeType;
};

const Fields = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
    display: flex;
    padding-top: 0.5rem;
  }
`;

const RadioButtonGroup = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
    display: flex;
    flex-grow: 1;
  }

  > div {
    position: relative;
    display: block;
    padding: 0 0 ${({ theme }) => theme.spacing.s};
    margin: 0;

    @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
      flex-grow: 1;
      display: inline-block;
      margin: 0 ${({ theme }) => theme.spacing.s} 0 0;
      padding: 0;
    }
  }

  label {
    display: block;
    cursor: pointer;
    height: 48px;
    font-family: ${({ theme }) => theme.typography.fontFamilyBase};
    font-size: 1.125rem;
    color: ${({ theme }) => theme.colors.primary};
    font-weight: normal;
    text-align: center;
    padding: 0.5rem 2rem;
    margin: 0;
    border-style: solid;
    border-width: ${({ theme }) => theme.button.buttonBorderThickness};
    border-color: ${({ theme }) => theme.colors.buttonPrimaryBorder};

    ${({ theme, formTheme }: AmountLabelProps) =>
      (formTheme === "SU2C" &&
        css`
          border-radius: ${theme.button.borderRadius};
          border-color: ${theme.colors.primary};
          font-weight: bold;
        `) ||
      css`
        border-color: ${theme.tokenColors.grey_200};
        background-color: ${theme.tokenColors.grey_200};
      `}
  }

  input {
    position: absolute;
    cursor: pointer;
    left: 0;
    top: 0;
    width: 100%;
    margin: 0;
    zoom: 1;
    opacity: 0;
    z-index: 1;

    &:focus,
    &:hover,
    &:checked {
      + label {
        background-color: ${({ theme }) => theme.colors.primary};
        border-color: ${({ theme }) => theme.colors.primary};
        color: ${({ theme }) => theme.colors.textLight};
      }
    }
  }
`;

const OtherAmountDiv = styled.div<{ extraTopSpacing: boolean }>`
  position: relative;
  margin-top: ${({ theme, extraTopSpacing }) =>
    extraTopSpacing ? theme.spacing.m : theme.spacing.s};

  @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
    margin-top: ${({ theme, extraTopSpacing }) =>
      extraTopSpacing ? theme.spacing.xs : theme.spacing.none};
  }

  label {
    > span:first-of-type {
      position: absolute;
      top: -${({ theme }) => theme.spacing.s};
      margin-bottom: ${({ theme }) => theme.spacing.none};
    }
  }

  .textField__icon {
    position: absolute;
    bottom: 12px;
    left: 15px;
    z-index: 3;
    font-weight: ${({ theme }) => theme.typography.fontWeightHeavy};
  }
`;

const TextFieldWrapper = styled.span`
  position: relative;
  display: block;
`;

const updateCheckedAmount = (
  nudges: Array<number>,
  radioAmount: string | number,
  handler: () => void
) => {
  const amount =
    typeof radioAmount === "string" ? parseInt(radioAmount, 10) : radioAmount;

  if (!Number.isNaN(amount) && !nudges?.includes(amount)) {
    handler();
  }
};

type AmountSelectionProps = FormPropsType & {
  canExperiment?: boolean;
};

const AmountSelection = ({
  values,
  errors,
  handleBlur,
  handleChange,
  touched,
  setFieldValue,
}: AmountSelectionProps): ReactElement => {
  const { donationData, setDonationData } = useContext(DonationContext);
  const { formVersion } = useContext(VersionsContext);
  const formName = useContext(FormNameContext);
  const formTheme = useContext(ThemeNameContext);
  const [checkedAmount, setCheckedAmount] = useState(
    values.donationAmount.amountRadioGroup
      ? values.donationAmount.amountRadioGroup
      : ""
  );
  const [checkedfrequency, setCheckedfrequency] = useState(
    values.frequency ? values.frequency : ""
  );
  const { pushError, pushInteraction } = useDataLayer();
  const formAdmin = getFormAdmin(formName);
  const formComponents = getFormComponents(formName);
  const nudges =
    formVersion === "regular"
      ? formComponents.donationAmount.regularNudges
      : formComponents.donationAmount.nudges;
  const [showButton, setShowButton] = useState(false);

  const buttonText = formVersion === "regular" ? "one-off" : "regular";
  const searchParams = new URLSearchParams(window.location.search);
  const urlAmount = searchParams.get("amount");
  const frequencyQueryString = searchParams.get("frequency");
  const buttonQueryString = searchParams;
  buttonQueryString.set(
    "type",
    formVersion === "regular" ? "single" : "regular"
  );
  if (formVersion === "regular" && buttonQueryString.has("frequency")) {
    buttonQueryString.delete("frequency");
  }
  if (buttonQueryString.has("amount")) buttonQueryString.delete("amount");
  const buttonUrl = buttonQueryString.toString();

  useEffect(() => {
    const urlAmountNum = Number(urlAmount);
    const donationDataVals = values;
    if (urlAmountNum > 0) {
      if (!nudges || !nudges.includes(urlAmountNum)) {
        donationDataVals.donationAmount.otherAmount = urlAmountNum.toFixed(2);
        donationDataVals.donationAmount.amountRadioGroup = "";
      } else {
        donationDataVals.donationAmount.amountRadioGroup =
          urlAmountNum.toString(10);
        donationDataVals.donationAmount.otherAmount = "";
      }
    }
    setDonationData(donationDataVals);
    if (typeof window !== "undefined") {
      sessionStorage.setItem(
        `formData-${formName}`,
        JSON.stringify(donationDataVals)
      );
    }
  }, []);

  useEffect(() => {
    const donationDataVals = values;
    const validFormVersions = getValidFormVersions(formAdmin);
    const isValidFrequency = donationFrequencies.filter((frequency) =>
      frequency.toLowerCase().includes(frequencyQueryString?.toLowerCase())
    );
    const hasButton =
      !!(formVersion === "regular" && validFormVersions.includes("single")) ||
      !!(formVersion === "single" && validFormVersions.includes("regular"));
    setShowButton(hasButton);

    if (isValidFrequency.length > 0) {
      const lowerCasefrequencyQueryString = frequencyQueryString.toLowerCase();
      const formattedFrequency =
        lowerCasefrequencyQueryString.charAt(0).toUpperCase() +
        lowerCasefrequencyQueryString.slice(1);
      setFieldValue("frequency", formattedFrequency);
      donationDataVals.frequency = formattedFrequency;
    }
    if (formVersion === "single") {
      setFieldValue("frequency", "");
      donationDataVals.frequency = "";
    }
    updateCheckedAmount(
      nudges,
      donationDataVals.donationAmount.amountRadioGroup,
      () => {
        setFieldValue("donationAmount.amountRadioGroup", "");
        donationDataVals.donationAmount.amountRadioGroup = "";
      }
    );
    setDonationData(donationDataVals);
    if (typeof window !== "undefined") {
      sessionStorage.setItem(
        `formData-${formName}`,
        JSON.stringify(donationDataVals)
      );
    }
  }, [formVersion]);

  useEffect(() => {
    setCheckedAmount(values.donationAmount.amountRadioGroup);
  }, [values.donationAmount.amountRadioGroup]);

  useEffect(() => {
    setCheckedfrequency(values.frequency);
  }, [values.frequency]);

  useEffect(() => {
    const errorDelay = setTimeout(() => {
      if (
        !!touched.donationAmount &&
        errors.donationAmount &&
        errors.donationAmount.otherAmount
      ) {
        const otherAmountError = errors.donationAmount.otherAmount as string;
        pushError("", "validation", `donationAmount: ${otherAmountError}`);
      }
    }, 1000);
    return (): void => clearTimeout(errorDelay);
  }, [errors.donationAmount, touched.donationAmount]);

  return (
    <FormGroup>
      <FormFields>
        <fieldset
          tabIndex={-1}
          aria-invalid={
            errors.donationAmount && touched.donationAmount ? "true" : null
          }
          aria-describedby={
            errors.donationAmount && touched.donationAmount
              ? "donationAmount-error"
              : null
          }
        >
          <legend>
            <Heading h2>Donation amount</Heading>
            <RequiredParagraph id="amountRadioGroupLabel">
              Please {nudges ? "choose" : "enter"} an amount for your{" "}
              {formVersion === "regular" ? "regular" : ""} donation.{" "}
              <span>(required)</span>
            </RequiredParagraph>
          </legend>
          <Fields>
            {nudges && nudges.length > 0 ? (
              <RadioButtonGroup
                formTheme={formTheme}
                id="amountRadioGroup"
                role="radiogroup"
                aria-required="true"
                aria-labelledby="amountRadioGroupLabel"
              >
                {nudges.map((nudge: number) => {
                  const amount = nudge.toString(10);

                  return (
                    <div key={`amount${amount}`}>
                      <Field name="donationAmount.amountRadioGroup">
                        {({ field, form }: FieldProps): ReactElement => (
                          <input
                            {...field}
                            id={`amount${amount}`}
                            type="radio"
                            value={amount}
                            data-cy={`amount-sel-${amount}`}
                            checked={amount === checkedAmount}
                            onBlur={(e): void => {
                              handleBlur(e);
                            }}
                            onChange={(e): void => {
                              form.setFieldValue(
                                "donationAmount.otherAmount",
                                ""
                              );
                              handleChange(e);
                              setDonationData(donationData);
                            }}
                          />
                        )}
                      </Field>
                      <label htmlFor={`amount${amount}`}>
                        £<span>{amount}</span>
                      </label>
                    </div>
                  );
                })}
              </RadioButtonGroup>
            ) : (
              ""
            )}
            <OtherAmountDiv extraTopSpacing={!nudges}>
              <Field name="donationAmount.otherAmount">
                {({ field, form }: FieldProps): ReactElement => (
                  <TextFieldWrapper>
                    <TextField
                      id="otherAmount"
                      data-testid="otherAmount"
                      value={values.donationAmount.otherAmount}
                      name={field.name}
                      label={nudges ? "Other amount" : "Amount"}
                      extraLeft="£"
                      aria-invalid={
                        errors.donationAmount && touched.donationAmount
                          ? errors.donationAmount.otherAmount &&
                            touched.donationAmount.otherAmount
                            ? "true"
                            : null
                          : null
                      }
                      aria-describedby={
                        errors.donationAmount && touched.donationAmount
                          ? errors.donationAmount.otherAmount &&
                            touched.donationAmount.otherAmount
                            ? "otherAmount-error"
                            : null
                          : null
                      }
                      onBlur={(e): void => {
                        if (
                          values.donationAmount.otherAmount &&
                          values.donationAmount.otherAmount > 0
                        ) {
                          form.setFieldValue(
                            field.name,
                            parseFloat(
                              values.donationAmount.otherAmount
                            ).toFixed(2)
                          );
                          form.setFieldTouched(field.name, true, false);
                        } else {
                          handleBlur(e);
                        }
                      }}
                      onChange={(e): void => {
                        if (e.target.value.length > 0) {
                          const roundedValue = /^-?\d+(?:\.\d{0,2})?/.exec(
                            e.target.value
                          )
                            ? /^-?\d+(?:\.\d{0,2})?/.exec(e.target.value)[0]
                            : e.target.value;
                          roundedValue
                            ? form.setFieldValue(field.name, roundedValue)
                            : form.setFieldValue(field.name, e.target.value);

                          nudges?.includes(Number(e.target.value))
                            ? form.setFieldValue(
                                "donationAmount.amountRadioGroup",
                                parseInt(e.target.value, 10).toString(10)
                              )
                            : form.setFieldValue(
                                "donationAmount.amountRadioGroup",
                                ""
                              );
                        } else {
                          handleChange(e);
                        }
                      }}
                    />
                    <span id="amountCurrency" className="textField__icon">
                      &pound;
                    </span>
                  </TextFieldWrapper>
                )}
              </Field>
            </OtherAmountDiv>
          </Fields>
          {touched.donationAmount && errors.donationAmount ? (
            <div role="alert" id="donationAmount-error">
              <FieldErrorMessage>
                {errors.donationAmount.amountRadioGroup ? (
                  errors.donationAmount.amountRadioGroup
                ) : (
                  <span id="otherAmount-error">
                    {errors.donationAmount.otherAmount}
                  </span>
                )}
              </FieldErrorMessage>
            </div>
          ) : (
            ""
          )}
        </fieldset>
        {formVersion === "single" && (
          <Box marginTop="xs">
            <StyledCollapse>
              <Collapse
                headerTitleText="How much of your donation comes to Cancer Research UK?"
                id="donationFeesCollapse"
                onOpenChange={(isOpen: boolean): void => {
                  pushInteraction(
                    "Accordion: Donation fees",
                    isOpen ? "Open" : "Close"
                  );
                }}
              >
                {formTheme === "SU2C" ? (
                  <Box paddingVertical="xs">
                    <Text>
                      100% of your donation goes to Cancer Research UK in
                      support of the Stand Up To Cancer campaign.
                    </Text>
                  </Box>
                ) : (
                  <Box paddingVertical="xs">
                    <Text>
                      The only fee deducted from your donation is the payment
                      processing fee. This is the fee we&rsquo;re charged by
                      your credit or debit card provider, or by PayPal UK when
                      you donate. The fee varies depending on how you choose to
                      pay.
                    </Text>
                    <Link
                      href="https://www.cancerresearchuk.org/get-involved/do-your-own-fundraising/practical-fundraising-help/set-up-a-cancer-research-uk-giving-page/fees-and-charges"
                      target="_blank"
                    >
                      Fees and charges for Cancer Research UK Giving Pages.
                    </Link>
                  </Box>
                )}
              </Collapse>
            </StyledCollapse>
          </Box>
        )}
        {formVersion === "regular" && (
          <fieldset
            aria-invalid={
              errors.donationfrequency && touched.donationfrequency
                ? "true"
                : null
            }
            aria-describedby={
              errors.donationfrequency && touched.donationfrequency
                ? "donationfrequency-error"
                : null
            }
          >
            <legend>
              <RequiredParagraph id="frequencyLabel">
                Tell us how often you&rsquo;d like to donate{" "}
                <span>(required)</span>
              </RequiredParagraph>
            </legend>
            <RadioButtonGroup
              formTheme={formTheme}
              id="frequency"
              role="radiogroup"
              aria-required="true"
              aria-labelledby="frequencyLabel"
            >
              {donationFrequencies.map((frequency: string) => (
                <div key={`frequency-${frequency}`}>
                  <Field name="frequency">
                    {({ field }: FieldProps): ReactElement => (
                      <input
                        {...field}
                        id={`frequency-${frequency}`}
                        type="radio"
                        value={frequency}
                        checked={frequency === checkedfrequency}
                        onBlur={(e): void => {
                          handleBlur(e);
                        }}
                        onChange={(e): void => {
                          handleChange(e);
                          setDonationData(donationData);
                        }}
                      />
                    )}
                  </Field>
                  <label htmlFor={`frequency-${frequency}`}>{frequency}</label>
                </div>
              ))}
            </RadioButtonGroup>
            {touched.frequency && errors.frequency && (
              <div role="alert" id="donationFrequency-error">
                <FieldErrorMessage>{errors.frequency}</FieldErrorMessage>
              </div>
            )}
          </fieldset>
        )}
        {showButton ? (
          <Box marginTop="xs">
            <Link
              onClick={(e) => {
                e.preventDefault();
                const href = `?${buttonUrl}`;

                window.location.href = href;
              }}
              href={`?${buttonUrl}`}
              appearance="primary"
            >
              Make a {buttonText} donation instead
            </Link>
          </Box>
        ) : (
          ""
        )}
      </FormFields>
    </FormGroup>
  );
};

export default AmountSelection;
